import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';

import { CLIENT_CONTEXT_TOKEN_DEFAULT } from '@/api/phs-api/tokens';

import { extractProblemDetailsBody } from './problem-details.util';

/**
 * Preserves RFC 9457 ProblemDetails bodies for PHS requests (ADR-014 D2, Q3b
 * — the DCS/PCS twin pattern).
 *
 * PHS 0.5.0 moved from the legacy `{ error: { code, … } }` envelope to
 * TOP-LEVEL ProblemDetails (`{ type, title, status, detail, code, details?,
 * correlation_id, error_id? }`). The pinned library `ErrorInterceptor`
 * unwraps ONLY the legacy shape, so every PHS 4xx would be rebuilt as
 * `{ code: 'HTTP_ERROR', message, message_key }` — silently killing every
 * conflict/404/protected/in-use branch on the prices, price-sources and FX
 * screens, and toasting the server's raw English prose behind the sheet.
 *
 * Errors propagate innermost-first, so this interceptor MUST be registered
 * AFTER `ErrorInterceptor` in `HTTP_INTERCEPTORS` (in `app.config.ts`,
 * alongside `PcsProblemDetailsInterceptor` and `DcsProblemDetailsInterceptor`).
 * It rethrows the ProblemDetails BODY as a plain object: the outer
 * `ErrorInterceptor`'s `instanceof HttpErrorResponse` check then fails and
 * passes it through untouched (no toast), while `toPriceHubError` keeps
 * reading top-level `code`/`correlation_id` unchanged.
 *
 * The defusing scope is deliberately narrow:
 * - only requests marked as PHS (`CLIENT_CONTEXT_TOKEN_DEFAULT` from
 *   `@/api/phs-api/tokens` — a DIFFERENT token instance than auth/pos's
 *   same-named ones; the generated PHS services and `PhsDeleteService` both
 *   set it, so raw deletes are covered too);
 * - only non-401 failures — 401 keeps the global logout/redirect flow;
 * - only ProblemDetails-shaped bodies (a record with a non-blank string
 *   `title`). The guard is the TITLE SHAPE, never a code allowlist: an
 *   unknown future backend code must still defuse the raw toast. DCS
 *   envelopes, HTML 5xx pages and empty bodies pass through untouched so the
 *   library's own handling keeps working.
 */
@Injectable()
export class PhsProblemDetailsInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!req.context.has(CLIENT_CONTEXT_TOKEN_DEFAULT)) {
      return next.handle(req);
    }
    return next
      .handle(req)
      .pipe(catchError((err: unknown) => throwError(() => extractProblemDetailsBody(err) ?? err)));
  }
}
